import { useCallback } from "react";
import { toast } from "sonner";

const MODULE_MAP = {
  friend: "friend",
  psychologist: "psychologist",
  therapist: "psychologist",
  mentor: "mentor",
  social: "social",
  guardian: "guardian",
  job: "guardian",
  career: "guardian",
  coach: "coach",
  life: "coach",
  storyteller: "storyteller",
  story: "storyteller",
};

const SHORTCUTS = [
  {
    patterns: [/^summarize\s*(this|conversation|chat)?$/i, /^sum\s*up$/i, /^give\s*me\s*a?\s*summary$/i],
    action: "summarize",
    label: "Summarize this",
  },
  {
    patterns: [/^switch\s*to\s+(\w+)$/i, /^change\s*to\s+(\w+)$/i, /^use\s+(\w+)\s*(mode|module)?$/i],
    action: "switch_module",
    label: "Switch to [module]",
  },
  {
    patterns: [/^new\s*(chat|conversation)$/i, /^start\s*(over|fresh|new)$/i],
    action: "new_chat",
    label: "New chat",
  },
  {
    patterns: [/^search\s+(.+)$/i, /^find\s+(.+)$/i],
    action: "search",
    label: "Search [query]",
  },
  {
    patterns: [/^read\s*(that|this|it|the\s*last\s*message)?$/i, /^play\s*(that|this|it)?$/i, /^speak$/i],
    action: "read_last",
    label: "Read that",
  },
  {
    patterns: [/^show\s*insights?$/i, /^emotional?\s*breakdown$/i, /^how\s*am\s*i\s*doing$/i],
    action: "insights",
    label: "Show insights",
  },
  {
    patterns: [/^dark\s*mode$/i, /^light\s*mode$/i, /^toggle\s*(dark|light)\s*mode$/i],
    action: "toggle_dark",
    label: "Dark/Light mode",
  },
];

export function useVoiceShortcuts({
  onSummarize,
  onSwitchModule,
  onNewChat,
  onSearch,
  onReadLast,
  onInsights,
  onToggleDark,
}) {
  const processVoiceInput = useCallback((text) => {
    const trimmed = text.trim();

    for (const shortcut of SHORTCUTS) {
      for (const pattern of shortcut.patterns) {
        const match = trimmed.match(pattern);
        if (match) {
          switch (shortcut.action) {
            case "summarize":
              if (onSummarize) { onSummarize(); toast.info("Summarizing conversation..."); return true; }
              break;
            case "switch_module": {
              const moduleName = (match[1] || "").toLowerCase();
              const moduleKey = MODULE_MAP[moduleName];
              if (moduleKey && onSwitchModule) {
                onSwitchModule(moduleKey);
                toast.info(`Switched to ${moduleKey} mode`);
                return true;
              }
              break;
            }
            case "new_chat":
              if (onNewChat) { onNewChat(); toast.info("Starting new conversation"); return true; }
              break;
            case "search": {
              const query = match[1]?.trim();
              if (query && onSearch) { onSearch(query); toast.info(`Searching: ${query}`); return true; }
              break;
            }
            case "read_last":
              if (onReadLast) { onReadLast(); return true; }
              break;
            case "insights":
              if (onInsights) { onInsights(); toast.info("Generating insights..."); return true; }
              break;
            case "toggle_dark":
              if (onToggleDark) { onToggleDark(); return true; }
              break;
            default:
              break;
          }
        }
      }
    }

    return false; // Not a voice shortcut — pass through as normal input
  }, [onSummarize, onSwitchModule, onNewChat, onSearch, onReadLast, onInsights, onToggleDark]);

  return { processVoiceInput, SHORTCUTS };
}
